import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  Slide,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useUser } from "../../contexts/userContext";
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';

import "dayjs/locale/zh-cn";
import { DOMAIN } from "../../config";
import SearchDialog from ".";
dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});


/**
 * SmartAvatar - 更健壮的图片切换逻辑
 *
 * 初始使用 tbUrl (.tb.jpg)
 * - 如果 tb.jpg 加载成功并且还没尝试 full，则尝试加载 full(.jpg)
 * - 如果 tb.jpg 加载失败且还没尝试 full，则尝试加载 full(.jpg)
 * - 如果 full(.jpg) 加载失败且还没回退 tb，则回退到 tb（只回退一次）
 *
 * 通过 triedFullRef / triedTbRef 避免循环请求。
 */
function SmartAvatar({ imgKey, alt, size = 56, sx = {} }) {
  const tbUrl = `${DOMAIN}miniapp/${imgKey}.tb.jpg`;
  const fullUrl = `${DOMAIN}miniapp/${imgKey}.jpg`;

  const [src, setSrc] = useState(tbUrl);
  const triedFullRef = useRef(false); // 是否已尝试过 full(.jpg)
  const triedTbRef = useRef(false); // 是否已尝试过回退到 tb（用于 full 失败回退）
  const mountedRef = useRef(true);

  useEffect(() => {
    // 当 imgKey 变更时，重置状态
    setSrc(tbUrl);
    triedFullRef.current = false;
    triedTbRef.current = false;
  }, [imgKey, tbUrl]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleLoad = (e) => {
    // 当 tb 加载成功且还没尝试 full，则去尝试 full
    try {
      const cur = e?.target?.src || "";
      if (cur.includes(".tb.jpg") && !triedFullRef.current) {
        // 标记为已尝试 full，替换 src 触发浏览器去加载 full
        triedFullRef.current = true;
        // 延迟一帧以避免某些浏览器同步问题
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          setSrc(fullUrl);
        });
      }
      // 如果加载的是 full 且成功，则什么都不做（保留 full）
    } catch (err) {
      // ignore
    }
  };

  const handleError = (e) => {
    const cur = e?.target?.src || "";

    // 如果当前失败的是 tb 且还没尝试过 full -> 尝试 full
    if (cur.includes(".tb.jpg") && !triedFullRef.current) {
      triedFullRef.current = true;
      setSrc(fullUrl);
      return;
    }

    // 如果当前失败的是 full 且还没回退过 tb -> 回退到 tb
    if (cur.includes(".jpg") && !cur.includes(".tb.jpg") && !triedTbRef.current) {
      triedTbRef.current = true;
      // 回退到 tb（如果 tb 也失败则不再尝试）
      setSrc(tbUrl);
      return;
    }

    // 其他情况：（例如 tb 已经失败过且 full 也失败过）——不再循环尝试
    // 可在这里做占位处理（如显示默认头像），但本组件保持简单不再进一步操作
  };

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onLoad={handleLoad}
      onError={handleError}
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        display: "block",
        bgcolor: "#eee",
        ...sx,
      }}
    />
  );
}

export default function BloggerListDialog({ open, onClose }) {
  const [bloggers, setBloggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const { userData } = useUser();
  const [sort, setSort] = useState("default"); // default 或 date
  // 搜索相关
    const [openSearch, setOpenSearch] = useState(false);
    const [chipTitle, setChipTitle] = useState(null);

  const limit = 5;

  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false); // 防止重复加载

  // 加载接口数据
  const loadData = async (p = 1) => {
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoading(true);

    try {
      const res = await fetch(
        `${DOMAIN}follow/userFollowings?userId=${userData.id}&page=${p}&limit=${limit}&sort=${sort}`
      );
      console.log(sort)
      const result = await res.json();

      setTotal(result.total || 0);
      if (p === 1) {
        setBloggers(result.data || []);
      } else {
        setBloggers((prev) => [...prev, ...(result.data || [])]);
      }
    } catch (e) {
      console.error("加载失败:", e);
    }

    setLoading(false);
    loadingMoreRef.current = false;
  };

  // 打开时加载第一页
  useEffect(() => {
    if (!open) return;
    setPage(1);
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userData?.id]);

  // 打开 Dialog 或 sort 改变时重载第一页
  useEffect(() => {
    if (!open) return;
    setPage(1);
    loadData(1);
  }, [open, sort]);

  // 下拉自动加载更多
  const onScroll = () => {
    const element = scrollRef.current;
    if (!element) return;

    const nearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    if (nearBottom && !loading && bloggers.length < total) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadData(nextPage);
    }
  };

  const handleSearch = (bloger)=>{
    setChipTitle(bloger)
    setOpenSearch(true)
  }

  return (
    <Box>
    {/* 搜索框 */}
      <SearchDialog
        openSearch={openSearch}
        setOpenSearch={setOpenSearch}
        chipTitle={chipTitle}
      />

    
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{
        "& .MuiDialog-paper": {
          width: 320,
          maxWidth: "90vw",
          ml: "auto",
          borderRadius: 0,
          bgcolor: "background.paper",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* 顶部栏 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 16 }}>
          所有关注博主
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Select
            size="small"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            sx={{
              borderRadius: 2,
              fontSize: 13,
              height: 28,
              minWidth: 120,
            }}
          >
            <MenuItem value="default">最近关注</MenuItem>
            <MenuItem value="date">最新更新</MenuItem>
          </Select>
          <IconButton size="small" color="inherit" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* 内容区域（监听滚动） */}
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          p: 1.5,
          overflowY: "auto",
          height: "calc(100% - 70px)",
          pb: 10,
        }}
      >
        {/* 第一页加载 Skeleton */}
        {loading && page === 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          bloggers.map((b, index) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card
                elevation={0}
                sx={{
                  mb: 1.2,
                  borderRadius: 2,
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                }}
              >
                <CardActionArea>
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      p: 1.5,
                    }}
                  >
                    {/* 使用 SmartAvatar */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        mr: 1.5,
                        flexShrink: 0,
                        borderRadius: "50%",
                        overflow: "hidden",
                        bgcolor: "#eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SmartAvatar imgKey={b.img_url} alt={b.bloger} size={56} />
                    </Box>

                    <Box
                        onClick={()=>handleSearch(b.bloger)}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontSize: 15 }}
                      >
                        {b.bloger}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: 13 }}
                      >
                        {b.platform} · {`${dayjs(b.date * 1000).fromNow()}更新`}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: 13,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          mt: 0.3,
                        }}
                      >
                        {b.caption}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </motion.div>
          ))
        )}

        {/* 加载更多中 */}
        {loading && page > 1 && (
          <Box sx={{ textAlign: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      {/* 底部固定栏（可自行添加取消关注按钮） */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          width: 320,
          maxWidth: "90vw",
          bgcolor: "#fff",
          p: 1.5,
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          gap: 1.5,
        }}
      ></Box>
    </Dialog>
    </Box>
  );
}
